import React, { useEffect, useState, useCallback } from 'react';
import Plus from 'lucide-react/dist/esm/icons/plus';
import MessageSquare from 'lucide-react/dist/esm/icons/message-square';
import Clock from 'lucide-react/dist/esm/icons/clock';
import Users from 'lucide-react/dist/esm/icons/users';
import Search from 'lucide-react/dist/esm/icons/search';
import X from 'lucide-react/dist/esm/icons/x';
import Trash2 from 'lucide-react/dist/esm/icons/trash-2';
import { useDiscoveryStore } from '../../stores/discoveryStore';
import { useUIStore } from '../../stores/uiStore';
import { getTranslation } from '../../i18n';
import { usePermissions, Permissions } from '../../hooks/usePermissions';
import { useOrgStore } from '../../stores/orgStore';
import { ConfirmationDialog } from '../../components/ui';
import type { DiscoveryStatus, DiscoveryRequestSummary } from './types';
import { formatMessageTime } from '../../utils/markdown';

const RequestBoard: React.FC = () => {
  const { requests, isLoading, error, loadRequests, loadRequest, createRequest, deleteRequest, setView } = useDiscoveryStore();
  const { language } = useUIStore();
  const t = (key: string) => getTranslation(language, key);

  const STATUS_FILTERS: { key: string; label: string }[] = [
    { key: 'all', label: t('discoveryBoardFilterAll') },
    { key: 'collecting', label: t('discoveryBoardFilterCollecting') },
    { key: 'spec_ready', label: t('discoveryBoardFilterSpecReady') },
    { key: 'submitted', label: t('discoveryBoardFilterSubmitted') },
    { key: 'in_progress', label: t('discoveryBoardFilterInProgress') },
  ];

  const STATUS_STYLES: Record<DiscoveryStatus, { bg: string; text: string; label: string }> = {
    draft: { bg: 'bg-gray-500/15', text: 'text-gray-400', label: t('discoveryStatusDraft') },
    collecting: { bg: 'bg-blue-500/15', text: 'text-blue-400', label: t('discoveryStatusCollecting') },
    spec_ready: { bg: 'bg-green-500/15', text: 'text-green-400', label: t('discoveryStatusSpecReady') },
    submitted: { bg: 'bg-purple-500/15', text: 'text-purple-400', label: t('discoveryStatusSubmitted') },
    in_progress: { bg: 'bg-amber-500/15', text: 'text-amber-400', label: t('discoveryStatusInProgress') },
    implemented: { bg: 'bg-emerald-500/15', text: 'text-emerald-400', label: t('discoveryStatusImplemented') },
    declined: { bg: 'bg-status-error/15', text: 'text-status-error', label: t('discoveryStatusDeclined') },
  };

  const { hasPermission } = usePermissions();
  const canManage = hasPermission(Permissions.MODULE_DISCOVERY_MANAGE);
  const currentUserId = useOrgStore((s) => s.userContext?.id);

  const [activeFilter, setActiveFilter] = useState('all');
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [newDescription, setNewDescription] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DiscoveryRequestSummary | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    loadRequests(activeFilter === 'all' ? undefined : activeFilter);
  }, [loadRequests, activeFilter]);

  const handleFilterChange = useCallback((key: string) => {
    setActiveFilter(key);
  }, []);

  const handleCardClick = useCallback((req: DiscoveryRequestSummary) => {
    loadRequest(req.id);
  }, [loadRequest]);

  const handleCreate = useCallback(async () => {
    const desc = newDescription.trim();
    if (!desc) return;
    if (desc.length < 3) { setCreateError('Description must be at least 3 characters.'); return; }
    setCreateError(null);
    setCreating(true);
    try {
      const created = await createRequest(desc);
      setShowNewDialog(false);
      setNewDescription('');
      loadRequest(created.id);
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      if (Array.isArray(detail) && detail[0]?.msg) {
        setCreateError(detail[0].msg);
      }
    } finally {
      setCreating(false);
    }
  }, [newDescription, createRequest, loadRequest]);

  const handleDialogKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleCreate();
      if (e.key === 'Escape') setShowNewDialog(false);
    },
    [handleCreate],
  );

  const handleDeleteClick = useCallback((e: React.MouseEvent, req: DiscoveryRequestSummary) => {
    e.stopPropagation();
    setDeleteTarget(req);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteRequest(deleteTarget.id);
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  }, [deleteTarget, deleteRequest]);

  return (
    <div className="h-full flex flex-col">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-subtle">
        <div className="flex items-center gap-2">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => handleFilterChange(f.key)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all duration-200 ${
                activeFilter === f.key
                  ? 'bg-brand/15 text-primary'
                  : 'text-content-muted hover:text-content-secondary hover:bg-surface-primary'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <button
          onClick={() => setShowNewDialog(true)}
          className="flex items-center gap-2 px-4 py-2 bg-brand/15 text-primary rounded-lg text-sm font-medium hover:bg-brand/25 transition-all duration-200"
        >
          <Plus size={16} />
          {t('discoveryBoardBtnNewRequest')}
        </button>
      </div>

      {/* Grid */}
      <div className="flex-1 overflow-y-auto px-6 py-4">
        {isLoading && requests.length === 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-40 rounded-xl bg-surface-primary animate-pulse" />
            ))}
          </div>
        ) : error && requests.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-content-muted text-sm">
            <p>{t('discoveryBoardFailedToLoad')}</p>
            <button
              onClick={() => loadRequests(activeFilter === 'all' ? undefined : activeFilter)}
              className="mt-3 px-4 py-2 rounded-lg bg-surface-secondary text-content-secondary hover:text-content hover:bg-surface-elevated transition-all text-xs"
            >
              {t('discoveryBoardBtnRetry')}
            </button>
          </div>
        ) : requests.length === 0 && !isLoading ? (
          <div className="flex flex-col items-center justify-center h-64 text-content-muted">
            <Search size={40} className="mb-4 opacity-30" />
            <p className="text-sm">{t('discoveryBoardNoneYet')}</p>
            <button
              onClick={() => setShowNewDialog(true)}
              className="mt-4 flex items-center gap-2 px-4 py-2 bg-brand/15 text-primary rounded-lg text-sm font-medium hover:bg-brand/25 transition-all"
            >
              <Plus size={16} />
              {t('discoveryBoardBtnCreateFirst')}
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {requests.map((req) => {
              const style = STATUS_STYLES[req.status] || STATUS_STYLES.draft;
              return (
                <div
                  key={req.id}
                  onClick={() => handleCardClick(req)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleCardClick(req); }}
                  className="relative flex flex-col gap-3 p-4 rounded-xl bg-surface-primary border border-subtle hover:bg-surface-secondary hover:border-default transition-all duration-200 text-left group cursor-pointer"
                >
                  {req.unread && (
                    <span className="absolute top-2.5 right-2.5 w-2.5 h-2.5 rounded-full bg-brand animate-pulse" />
                  )}

                  <div className="flex items-start justify-between gap-2">
                    <h3 className="text-sm font-medium text-content line-clamp-2 flex-1">
                      {req.title || req.description || t('discoveryBoardCardUntitledRequest')}
                    </h3>
                    <span className={`shrink-0 px-2 py-0.5 rounded-full text-[10px] font-semibold ${style.bg} ${style.text}`}>
                      {style.label}
                    </span>
                  </div>

                  {req.description && (
                    <p className="text-xs text-content-muted line-clamp-2 leading-relaxed">{req.description}</p>
                  )}

                  <div className="flex items-center gap-4 mt-auto pt-2 border-t border-subtle">
                    <span className="text-[11px] text-content-muted truncate max-w-[120px]">
                      {req.initiator_name || req.initiator_email || t('discoveryBoardCardUnknownInitiator')}
                    </span>
                    <div className="flex items-center gap-3 ml-auto text-[11px] text-content-muted">
                      <span className="flex items-center gap-1">
                        <Users size={12} />
                        {req.stakeholder_count}
                      </span>
                      <span className="flex items-center gap-1">
                        <MessageSquare size={12} />
                        {req.message_count}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock size={12} />
                        {formatMessageTime(req.updated_at)}
                      </span>
                      {(canManage || req.initiated_by === currentUserId) && (
                        <button
                          onClick={(e) => handleDeleteClick(e, req)}
                          title="Delete request"
                          className="flex items-center justify-center text-content-muted hover:text-status-error transition-colors duration-150"
                        >
                          <Trash2 size={12} />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Delete Confirmation */}
      <ConfirmationDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete Request"
        message={`Are you sure you want to delete "${deleteTarget?.title || deleteTarget?.description || 'this request'}"? This action cannot be undone.`}
        confirmText={deleting ? 'Deleting…' : 'Delete'}
        cancelText="Cancel"
        variant="danger"
      />

      {/* New Request Dialog */}
      {showNewDialog && (
        <div className="fixed inset-0 z-modal flex items-center justify-center bg-surface-overlay backdrop-blur-sm animate-fade-in-down">
          <div
            className="w-full max-w-lg mx-4 bg-surface-page border border-subtle rounded-2xl shadow-2xl animate-fade-in-down"
            onKeyDown={handleDialogKeyDown}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-subtle">
              <h2 className="text-base font-semibold text-content">{t('discoveryBoardNewRequestDialogTitle')}</h2>
              <button
                onClick={() => { setShowNewDialog(false); setCreateError(null); setNewDescription(''); }}
                className="flex items-center justify-center w-8 h-8 rounded-lg text-content-muted hover:text-content hover:bg-surface-secondary transition-all"
              >
                <X size={18} />
              </button>
            </div>
            <div className="p-6">
              <label className="block text-xs text-content-tertiary mb-2 font-medium">
                {t('discoveryBoardNewRequestLabel')}
              </label>
              <textarea
                autoFocus
                rows={4}
                value={newDescription}
                onChange={(e) => { setNewDescription(e.target.value); setCreateError(null); }}
                placeholder="e.g. We need a dashboard that shows real-time sales data for all regions..."
                className={`w-full px-4 py-3 rounded-xl bg-surface-primary border text-content text-sm placeholder:text-content-faint resize-none focus:outline-none focus:bg-surface-secondary transition-all ${createError ? 'border-status-error/60 focus:border-status-error/60' : 'border-subtle focus:border-brand/40'}`}
              />
              {createError && (
                <p className="mt-1.5 text-xs text-status-error">{createError}</p>
              )}
              <div className="flex justify-end gap-3 mt-4">
                <button
                  onClick={() => { setShowNewDialog(false); setCreateError(null); setNewDescription(''); }}
                  className="px-4 py-2 rounded-lg text-sm text-content-tertiary hover:text-content hover:bg-surface-secondary transition-all"
                >
                  {t('discoveryBoardBtnCancelDialog')}
                </button>
                <button
                  onClick={handleCreate}
                  disabled={!newDescription.trim() || creating}
                  className="flex items-center gap-2 px-5 py-2 rounded-lg bg-brand text-content text-sm font-medium hover:bg-brand/80 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                >
                  {creating ? (
                    <span className="w-4 h-4 border-2 border-content/30 border-t-content rounded-full animate-spin" />
                  ) : (
                    <Plus size={16} />
                  )}
                  {t('discoveryBoardBtnCreateRequest')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default React.memo(RequestBoard);
